import chroma from "chroma-js";
import { Appearance } from "common/generator/components/button-appearance";
import type { WireHintComponent } from "common/generator/components/fluent-components";
import { Mood } from "common/generator/components/mood";
import { isDefined, isEmoji } from "common/Support";
import { UIBackgroundStyle } from "common/wire/types";
import { useWireAppTheme } from "hooks/use-wireapp-theme";
import React from "react";
import tw from "twin.macro";
import { css } from "webapp/styled-components";
import { makeSingleActionSpreadProps } from "webapp/wire-renderers/wire-lib";
import type { WireRenderer } from "webapp/wire-renderers/wire-renderer";
import { WireButton } from "../wire-button/wire-button";
import { SectionStyleProvider, useSectionStyle } from "../wire-container/wire-container";

export const WireHint: WireRenderer<WireHintComponent> = React.memo(props => {
    const { backend, mood, action, actionTitle, description, emoji } = props;

    const theme = useWireAppTheme();

    const parentSectionStyle = useSectionStyle();
    const sectionStyle =
        mood === Mood.Default || mood === Mood.Neutral ? UIBackgroundStyle.None : UIBackgroundStyle.Highlight;
    const transparentAccent =
        parentSectionStyle === UIBackgroundStyle.Accent
            ? "rgba(255, 255, 255, 0.2)"
            : chroma(theme.primaryAccentColor).alpha(0.2).css();

    return (
        <SectionStyleProvider value={sectionStyle}>
            <div
                className={mood}
                css={css`
                    background-color: ${transparentAccent};

                    &.neutral {
                        ${parentSectionStyle !== UIBackgroundStyle.Accent && tw`bg-n200A`}
                    }

                    &.warning {
                        ${tw`bg-y100`}
                        ${tw`text-y500`}
                    }

                    &.error {
                        ${tw`bg-r100`}
                        ${tw`text-r500`}
                    }

                    &.success {
                        ${tw`bg-g100`}
                        ${tw`text-g500`}
                    }
                `}
                tw="relative text-base text-text-contextual-dark px-6 py-4 flex flex-col rounded-lg whitespace-pre-wrap
                    gp-md:(flex-row items-center)">
                <div tw="flex-1 flex">
                    {emoji !== undefined && isEmoji(emoji) && <div tw="flex-shrink-0 mr-2">{emoji}</div>}
                    <div tw="flex-shrink">{description}</div>
                </div>
                {isDefined(action) && (
                    <WireButton
                        data-testid="wh-button"
                        tw="ml-2 mt-1
                            gp-md:mt-0"
                        {...makeSingleActionSpreadProps(action, backend)}
                        appearance={Appearance.Bordered}>
                        {actionTitle}
                    </WireButton>
                )}
            </div>
        </SectionStyleProvider>
    );
});
